import React, { createContext, use, useContext, useEffect, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { appInsights } from 'app-insights';
import AuthProviderContext from 'shared/features/auth/authentication/providers/contexts/auth-provider-context';
import { useLazyGetMinimumLogLevel } from 'shared/logging/providers/configuration-queries';

interface ILoggingContext {
  log: (message: string, severityLevel: SeverityLevel) => void;
}

const LoggingContext = createContext<ILoggingContext>({} as ILoggingContext);

const useLogging = () => useContext(LoggingContext);

const LoggingProvider = ({ children }: Readonly<{ children: React.ReactNode }>): React.JSX.Element => {
  const { user } = useAuth();
  const {
    tenant: { name: tenantName },
  } = use(AuthProviderContext);

  const { refetch: triggerGetMinimumLogLevelQuery, data } = useLazyGetMinimumLogLevel();

  useEffect(() => {
    if (tenantName && user?.access_token) {
      triggerGetMinimumLogLevelQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantName, user?.access_token]);

  const value: ILoggingContext = useMemo(() => {
    const log = (message: string, severityLevel: SeverityLevel) => {
      let minimumLogLevel: SeverityLevel = SeverityLevel.Information;

      switch (data?.minimumLogLevel?.toLowerCase()) {
        case 'warning':
          minimumLogLevel = SeverityLevel.Warning;
          break;
        case 'critical':
          minimumLogLevel = SeverityLevel.Critical;
          break;
        case 'error':
          minimumLogLevel = SeverityLevel.Error;
          break;
        case 'trace':
          minimumLogLevel = SeverityLevel.Verbose;
          break;
      }

      if (severityLevel >= minimumLogLevel || minimumLogLevel === SeverityLevel.Verbose) {
        appInsights.trackTrace({ message: message, severityLevel: severityLevel }, { ApplicationName: 'Sonevo Cloud Portal' });
      }
    };

    return {
      log,
    };
  }, [data]);

  return <LoggingContext.Provider value={value}>{children}</LoggingContext.Provider>;
};

export { LoggingProvider, useLogging };
